import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import fetchAPI from '../fetchAPI';

/**
 * Company-scoped CRM credential provisioning.
 *
 * The token is write-only end to end: nothing here can read one back, because
 * the API never returns it. What comes back is whether one is set and when it
 * was last verified.
 */

export interface CrmCredentialStatus {
  crm: string | null;
  /** False for per-user CRMs (Reapit, GHL) — there is no company token to set. */
  manageable: boolean;
  reason?: string;
  present?: boolean;
  lastValidatedAt?: string | null;
  /** False when CRM_CREDENTIALS_KEY is absent — saving will be refused. */
  encryptionReady: boolean;
}

export interface CrmBranchTeam {
  id: string;
  name: string;
  memberCount: number;
}

export interface CrmBranch {
  /** The CRM's own branch id — Street's branch_uuid. */
  externalId: string;
  name: string;
  details: Record<string, unknown> | null;
  /** True when the CRM's name has moved on from the mapped team's. */
  renamedInCrm: boolean;
  /** The team negotiators are assigned to for this branch, once one is mapped. */
  team: CrmBranchTeam | null;
}

export interface CompanyTeamOption {
  id: string;
  teamName: string;
  crmExternalId: string | null;
  crmSyncedAt: string | null;
  memberCount: number;
}

export interface CrmBranchList {
  /** False when the company's CRM has no branches — every field below is empty. */
  supported: boolean;
  crmId: string | null;
  /** Fetched live from the CRM; nothing is mirrored locally. */
  branches: CrmBranch[];
  teams: CompanyTeamOption[];
  /** Teams mapped to a branch the CRM no longer returns. Reported, never acted on. */
  stale: Array<{ id: string; name: string }>;
}

const CREDENTIAL_KEY = 'crm/admin/credential';
const BRANCHES_KEY = 'crm/admin/branches';

export const useCrmCredential = (companyId?: string) =>
  useQuery({
    queryKey: [CREDENTIAL_KEY, companyId],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const response: iResponse<CrmCredentialStatus> = await fetchAPI({
        method: 'GET',
        route: 'crm/admin/credential',
        queryString: new URLSearchParams({ companyId: companyId as string }).toString()
      });
      return response.data;
    }
  });

export const useCrmBranches = (companyId?: string, enabled = true) =>
  useQuery({
    queryKey: [BRANCHES_KEY, companyId],
    enabled: Boolean(companyId) && enabled,
    queryFn: async () => {
      const response: iResponse<CrmBranchList> = await fetchAPI({
        method: 'GET',
        route: 'crm/admin/branches',
        queryString: new URLSearchParams({ companyId: companyId as string }).toString()
      });
      return response.data ?? null;
    }
  });

export interface SaveTokenResult {
  saved: boolean;
  valid: boolean;
  detail: string;
  /** True when the account had no token before this one. */
  firstSave?: boolean;
  /** True when the CRM supports the branches + users sync (Street). */
  syncAvailable?: boolean;
}

/**
 * Save a token. The server validates against the CRM first and stores nothing
 * on failure, so a rejected token leaves the previous state untouched.
 */
export const useSaveCrmCredential = (companyId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const response: iResponse<SaveTokenResult> = await fetchAPI({
        method: 'POST',
        route: 'crm/admin/credential',
        data: { companyId, token } as APIData
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CREDENTIAL_KEY, companyId] });
      // A newly valid token usually means branches can now be pulled.
      queryClient.invalidateQueries({ queryKey: [BRANCHES_KEY, companyId] });
    }
  });
};

/**
 * Map a branch to a team the agency already has.
 *
 * The server refuses (409) rather than silently rearranging anything when the
 * link would cost a team its members — the message it returns names the reason,
 * so it is shown verbatim rather than replaced with a generic failure.
 */
export const useLinkBranchTeam = (companyId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ externalId, teamId }: { externalId: string; teamId: string }) => {
      const response: iResponse<{ mapped: boolean; changed: boolean }> = await fetchAPI({
        method: 'POST',
        route: 'crm/admin/branches/link-team',
        data: { companyId, externalId, teamId } as APIData
      });
      // fetchAPI returns 4xx as data rather than throwing, so a refusal would
      // otherwise land in onSuccess and look like the link worked.
      if (response.code >= 400) throw new Error(response.error?.message ?? 'Could not link that team.');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BRANCHES_KEY, companyId] });
      // The team list shows which teams are CRM-linked, so it is now stale too.
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    }
  });
};

/** Break a branch link. The team and everyone in it are untouched. */
export const useUnlinkBranchTeam = (companyId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId }: { teamId: string }) => {
      const response: iResponse<{ unlinked: boolean }> = await fetchAPI({
        method: 'POST',
        route: 'crm/admin/branches/unlink-team',
        data: { companyId, teamId } as APIData
      });
      if (response.code >= 400) throw new Error(response.error?.message ?? 'Could not unlink that team.');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BRANCHES_KEY, companyId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    }
  });
};

export interface CrmOption {
  id: string;
  name: string;
  scope: 'user' | 'company';
  loginMode: string;
  /** True when onboarding requires an agency-wide token rather than per-user OAuth. */
  requiresCompanyToken: boolean;
}

/** CRMs a company can be put on, annotated with what each adapter declares. */
export const useCrmList = () =>
  useQuery({
    queryKey: ['crm/admin/crms'],
    queryFn: async () => {
      const response: iResponse<{ data: CrmOption[] }> = await fetchAPI({
        method: 'GET',
        route: 'crm/admin/crms'
      });
      return response.data?.data ?? [];
    }
  });

export interface NewNegotiatorAccount {
  name: string;
  crmId: string;
  /** Optional company details. Blank strings are dropped rather than stored:
   *  Companies.website carries @IsUrl, and Sequelize runs a validator on ""
   *  while skipping it on null. */
  website?: string;
  address?: string;
}

export const useCreateNegotiatorAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: NewNegotiatorAccount) => {
      const response: iResponse<{ data: { id: string; name: string } }> = await fetchAPI({
        method: 'POST',
        route: 'crm/admin/company',
        data: payload as APIData
      });
      return response.data?.data;
    },
    // The new account must appear in the All Accounts list behind the dialog.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['crm/messages/company-engagement'] })
  });
};

export interface StreetSyncPreview {
  branches: {
    total: number;
    alreadyMapped: number;
    toCreate: Array<{ externalId: string; teamName: string }>;
    toAdopt: Array<{ externalId: string; teamId: string; teamName: string }>;
    /** Mapped branches Street has renamed. Reported; the team keeps its own name. */
    renamedInCrm: Array<{ branchName: string; teamName: string }>;
    /** Teams mapped to a branch Street no longer lists. Reported, never acted on. */
    stale: Array<{ teamId: string; teamName: string }>;
  };
  users: {
    total: number;
    deactivated: number;
    withoutEmail: number;
    alreadyInAccount: number;
    toAssignTeam: number;
    inOtherAccount: Array<{ email: string; name: string }>;
    previouslyDeleted: Array<{ email: string; name: string }>;
    toCreate: Array<{ email: string; name: string; jobTitle: string | null; branchName: string | null }>;
  };
}

export interface StreetSyncResult {
  teams: {
    created: Array<{ id: string; teamName: string; externalId: string }>;
    adopted: Array<{ id: string; teamName: string; externalId: string }>;
    skipped: Array<{ externalId: string; name: string; reason: string }>;
    /** Existing mappings Street still lists, stamped as confirmed. */
    confirmed: number;
  };
  users: { created: number; teamAssigned: number; skipped: Array<{ email: string; reason: string }> };
  preview: StreetSyncPreview;
}

/**
 * What a Street sync would create. Read-only on the server, and fetched only
 * while the confirmation dialog is open — it reads Street live every time.
 */
export const useStreetSyncPreview = (companyId?: string, enabled = false) =>
  useQuery({
    queryKey: ['crm/admin/street-sync/preview', companyId],
    enabled: Boolean(companyId) && enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    queryFn: async () => {
      const response: iResponse<StreetSyncPreview> = await fetchAPI({
        method: 'GET',
        route: 'crm/admin/street-sync/preview',
        queryString: new URLSearchParams({ companyId: companyId as string }).toString()
      });
      if (response.code >= 400 || !response.data) {
        throw new Error(response.error?.message ?? 'Could not read the account from Street.');
      }
      return response.data;
    }
  });

export const useApplyStreetSync = (companyId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response: iResponse<StreetSyncResult> = await fetchAPI({
        method: 'POST',
        route: 'crm/admin/street-sync',
        data: { companyId } as APIData
      });
      if (response.code >= 400 || !response.data) {
        throw new Error(response.error?.message ?? 'Street sync failed. Nothing may have been saved.');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BRANCHES_KEY, companyId] });
      // New teams and users show on the account page behind the drawer.
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['billing/teams', companyId] });
      queryClient.invalidateQueries({ queryKey: ['billing/total-users', companyId] });
      queryClient.invalidateQueries({ queryKey: ['billing/company-users-first', companyId] });
    }
  });
};
